import {
  Component,
  HostBinding,
  Input,
  OnInit,
  OnChanges,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { NavItem } from './nav-item';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { NavService } from 'src/app/shared/services/nav.service';
import { SideMenuService } from '../../../../../modules/sys/services/side-menu.service';

@Component({
  selector: 'app-nav-item',
  imports: [TranslateModule, TablerIconsModule, MaterialModule, CommonModule],
  templateUrl: './nav-item.component.html',
  styleUrl: './nav-item.component.css',
  animations: [
    trigger('indicatorRotate', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4,0.0,0.2,1)')
      ),
    ]),
  ],
})
export class AppNavItemComponent implements OnInit, OnChanges, OnDestroy {
  @Output() toggleMobileLink: any = new EventEmitter<void>();
  @Output() notify: EventEmitter<boolean> = new EventEmitter<boolean>();

  expanded: any = false;
  disabled: any = false;
  twoLines: any = false;
  @HostBinding('attr.aria-expanded') ariaExpanded = this.expanded;
  @Input() item: NavItem | any;
  @Input() depth: any;
  langCode: any;

  // Pin functionality
  isPinned: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    public navService: NavService,
    public router: Router,
    private menuService: SideMenuService
  ) {
    if (this.depth === undefined) {
      this.depth = 0;
    }

    let lang: any = localStorage.getItem('lang');
    let langObj: any;
    if (lang != undefined && lang != null) {
      langObj = JSON.parse(lang);
      this.langCode = langObj.code;
    } else {
      this.langCode = 'ar';
    }
  }

  ngOnInit() {
    this.checkIfExpanded(this.router.url);
    this.updatePinStatus();

    if (this.item?.expanded === true) {
      this.expanded = true;
    }

    this.menuService.getPinnedItemsObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePinStatus();
      });
  }

  ngOnChanges() {
    this.checkIfExpanded(this.router.url);
    this.updatePinStatus();

    if (this.item?.expanded === true) {
      this.expanded = true;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkIfExpanded(url: string) {
    this.expanded = this.hasActiveRoute(this.item, url);
    this.ariaExpanded = this.expanded;
  }

  hasActiveRoute(item: NavItem, url: string): boolean {
    // Exact match for THIS item
    if (item.route && this.router.isActive(item.route, false)) {
      return true;
    }
    // Check all children
    if (item.children && item.children.length) {
      for (const child of item.children) {
        if (this.hasActiveRoute(child, url)) {
          return true; // If ANY child matches, parent must expand
        }
      }
    }

    return false;
  }

  onItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      this.router.navigate([item.route]);
    }
    if (item.children && item.children.length) {
      this.expanded = !this.expanded;
    }
    //scroll
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    if (!this.expanded) {
      if (window.innerWidth < 1024) {
        this.notify.emit();
      }
    }
  }

  onSubItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      if (this.expanded && window.innerWidth < 1024) {
        this.notify.emit();
      }
    }
  }

  // Updated method to properly check and update pin status
  updatePinStatus(): void {
    if (this.item?.id) {
      const wasPinned = this.isPinned;



      if (this.item.isPinned !== undefined) {
        this.isPinned = this.item.isPinned;
      } else {
        // Check service for active pinned item
        const servicePinnedItem = this.menuService.getPinnedItem(this.item.id);
        const isActivelyPinned = servicePinnedItem && servicePinnedItem.isActive !== false;

        this.isPinned = !!(isActivelyPinned && this.item.userSideMenuIsActive === true);
      }

      if (wasPinned !== this.isPinned) {
      }
    } else {
      this.isPinned = false;
    }
  }


  shouldShowPinButton(): boolean {
    // Show pin button for ALL levels/depths - every menu item can be pinned
    return true;
  }


  togglePin(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.item?.id) {
      console.error('Item ID is missing');
      return;
    }

    if (this.item.isProcessingPin) {
      return;
    }


    this.item.isProcessingPin = true;

    // Use the most reliable source for current pin status
    const isCurrentlyPinned = this.item.isPinned === true ||
      (this.item.userSideMenuIsActive === true && this.menuService.isPinned(this.item.id));

    if (isCurrentlyPinned) {
      // UNPIN LOGIC - Find the active pinned record and deactivate it

      // Get the active pinned item record
      const pinnedItem = this.menuService.getPinnedItem(this.item.id);

      if (pinnedItem && pinnedItem.id) {

        this.menuService.unpinItemById(pinnedItem.id).subscribe({
          next: (response) => {

            // Update local state immediately for UI responsiveness
            this.isPinned = false;
            this.item.isPinned = false;
            this.item.userSideMenuIsActive = false;

            // Small delay to ensure API state is updated before reorganizing
            setTimeout(() => {
              // Force a menu reorganization to restore item to original position
              // This will be handled by the service's finalize() block
            }, 100);
          },
          error: (error) => {
            console.error('Error unpinning item:', error);
            // Let the service refresh handle the final state
          },
          complete: () => {
            this.item.isProcessingPin = false;
          }
        });
      } else {
        // No active pinned record found, just update local state
        this.isPinned = false;
        this.item.isPinned = false;
        this.item.userSideMenuIsActive = false;
        this.item.isProcessingPin = false;
      }

    } else {
      // PIN LOGIC - Create new pinned record or reactivate existing inactive one

      this.menuService.pinItemById(this.item.id).subscribe({
        next: (response) => {

          // Update local state immediately for UI responsiveness
          this.isPinned = true;
          this.item.isPinned = true;
          this.item.userSideMenuIsActive = true;
        },
        error: (error) => {
          console.error('Error pinning item:', error);

          // Revert optimistic update on error
          this.isPinned = false;
          this.item.isPinned = false;
          this.item.userSideMenuIsActive = false;
        },
        complete: () => {
          this.item.isProcessingPin = false;
        }
      });
    }
  }


}